import React from 'react';
import { path } from './client/routes.js';
import { Button } from '@fabric-ds/react';
import Score from './client/score.js';
import Verified from './client/verified.js';
import HiddenProfileName from './client/HiddenProfileName.js';
import { Profile, Reputation } from './client/types.js';
import Avatar from './client/Avatar.js';
import { InfoTooltip } from './client/ToolTip.js';

export interface IAppProps {
    loggedIn: boolean,
    ownerNotAvailable?: boolean,
    userId?: number,
    finnKode: number,
    imageUrl?: string,
    anonymous?: boolean,
    isPrivateDealer?: boolean,
    showContactButton?: boolean
    description?: string,
    verified?: boolean,
    reputation?: Reputation,
    apis?: {
        baseUrl: string
    },
    finntoken?: string
}


const App: React.FunctionComponent<IAppProps> = ({ loggedIn, userId, description, reputation, verified, showContactButton, finnKode, apis, isPrivateDealer, anonymous, ownerNotAvailable, finntoken }) => {
    const [showPersonalData, setShowPersonalData] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [profile, setProfile] = React.useState<Profile | undefined>(undefined);

    const fetchProfile = async (userId: number, finnKode: number, loading: boolean) => {
        if (!loading && !anonymous) {
            try {
                setLoading(true);
                const response = await fetch(`${apis?.baseUrl}/trust-api/profile/${userId}/${finnKode}`, {
                    headers: {
                        finntoken: finntoken
                    }
                });
                if (!response.ok) {
                    throw new Error(`Request failed: ${response.status} ${response.statusText}`);
                };
                const text = await response.text();
                try {
                    const data = JSON.parse(text);
                    setProfile(data);
                    setShowPersonalData(true);
                    setLoading(false);
                } catch (error) {
                    throw new Error(`Request failed: ${response.status} ${response.text}`);
                }
            } // throw error all the way to trackJS
            finally {
                setLoading(false)
            }
        } else {
            setShowPersonalData(true);
        }
    }

    if (!loggedIn) {
        return <div className='bg-white p-16 rounded-8 border border-bluegray-200'>
            <h2 className='hidden'>Profilinfo</h2>
            <div className='flex gap-8 items-center'>
                <Avatar alt="Generisk profilbilde" className="w-64 h-64 rounded-full block shrink-0" />
                <p className="m-0">Du må være logget inn for å se profilen og sende melding</p>
            </div>
            <Button small className='mt-16 max-w-full w-full' href={`/auth/login`}>Logg inn</Button>
        </div>
    }

    if (ownerNotAvailable) {
        return <div className='bg-white p-16 rounded-8 border border-bluegray-200'>
            <h2 className='hidden'>Profilinfo</h2>
            <div className='flex gap-8'>
                <Avatar alt="Generisk profilbilde" className="w-64 h-64 rounded-full block" />
                <div>
                    <div className='flex space-between'>
                        <Button className='text-16 leading-22' link>
                            <strong className='flex items-center gap-8'>Profilinformasjon</strong>
                        </Button>
                        <InfoTooltip title="Skjult profilinformasjon">
                            Brukernavn er ikke tilgjengelig akkurat nå, men du kan kontakte selgere gjennom meldingstjenesten.
                        </InfoTooltip>
                    </div>
                    <div className='bg-bluegray-200 w-full h-12 m-0 mt-2 rounded-4'></div>
                    <div className='bg-bluegray-200 w-2/3 h-12 m-0 mt-6 rounded-4'></div>
                </div>
            </div>
            {
                showContactButton && (
                    <div className='mt-16'>
                        { /**  @ts-ignore */}
                        <finn-contact-button buttonVariant='SECONDARY' buttonText="Kontakt selger" size="SMALL"></finn-contact-button>
                    </div>
                )
            }
        </div>
    }

    const privateDealer = isPrivateDealer ? <p className='text-14 font-medium leading-18 m-0 mb-8'>Privat forhandler</p> : null;

    return <>
        {privateDealer}
        <div className='bg-white p-16 rounded-8 border border-bluegray-200'>
            <h2 className='hidden'>Profilinfo</h2>
            <div className='flex gap-8'>
                {
                    (showPersonalData && !anonymous && profile && profile?.identity.avatar && profile?.identity.avatar !== "") ?
                        <>
                            <img className='w-64 h-64 rounded-full z-10' src={profile?.identity.avatar} alt={`Profilbilde for ${profile?.identity.name}`} />
                            <Avatar alt="Generisk profilbilde" className="w-64 h-64 rounded-full absolute" />
                        </>
                        :
                        <Avatar alt="Generisk profilbilde" className="w-64 h-64 rounded-full block" />
                }
                <div>
                    {
                        !showPersonalData && !profile ?
                            <Button className='text-16 leading-22' link onClick={() => fetchProfile(userId, finnKode, loading)}>
                                <strong className='flex items-center gap-8'>Vis profilinfo</strong>
                            </Button> :
                            !anonymous ?
                                <a className="text-16 leading-22" href={path.profile(`${userId}`)}><strong>{profile?.identity.name}</strong></a> :
                                <HiddenProfileName />
                    }
                    <Verified verified={!!verified} />
                    <p className='text-14 leading-18 m-0 mt-2'>{description}</p>
                    {reputation?.feedback &&
                        <div className='mt-8 flex items-center'>
                            <Score score={reputation?.feedback.overallScore} />
                            {!anonymous && profile ?
                                <a className='text-14 leading-18' href={path.profile(`${userId}`)}><strong>{reputation?.feedback.receivedCount} vurderinger </strong></a> :
                                <p className='text-14 leading-18 inline-block m-0'>{reputation?.feedback.receivedCount} vurderinger </p>
                            }
                        </div>
                    }
                </div>
            </div>
            {
                showContactButton && (
                    <div className='mt-16'>
                        { /**  @ts-ignore */}
                        <finn-contact-button buttonVariant='SECONDARY' buttonText="Kontakt selger" size="SMALL"></finn-contact-button>
                    </div>
                )}
        </div>
    </>
};

export default App;

